import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface HeroContent {
  title: {
    line1: { en: string; ar: string };
    line2: { en: string; ar: string };
  };
  subtitle: { en: string; ar: string };
  ctaButton: { en: string; ar: string };
  learnMoreButton: { en: string; ar: string };
  stats: {
    booksPublished: { value: string; label: { en: string; ar: string } };
    publishers: { value: string; label: { en: string; ar: string } };
    satisfaction: { value: string; label: { en: string; ar: string } };
  };
}

export interface AboutContent {
  title: { en: string; ar: string };
  subtitle: { en: string; ar: string };
  description: { en: string; ar: string };
}

export interface ServiceItem {
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  icon: string;
}

export interface ServicesContent {
  title: { en: string; ar: string };
  subtitle: { en: string; ar: string };
  services: ServiceItem[];
}

export interface HowItWorksStep {
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  icon: string;
}

export interface HowItWorksContent {
  title: { en: string; ar: string };
  subtitle: { en: string; ar: string };
  videoUrl?: string;
  steps: HowItWorksStep[];
}

export interface AboutArabookverseContent {
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  vision: {
    title: { en: string; ar: string };
    description: { en: string; ar: string };
  };
  mission: {
    title: { en: string; ar: string };
    description: { en: string; ar: string };
  };
  purpose: {
    title: { en: string; ar: string };
    items: { en: string; ar: string }[];
  };
}

export interface ContactContent {
  title: { en: string; ar: string };
  subtitle: { en: string; ar: string };
  email: string;
  phone: string;
  address: { en: string; ar: string };
}

export interface WebsiteContent {
  settings?: {
    defaultLanguage: string;
  };
  hero: HeroContent;
  about: AboutContent;
  aboutArabookverse: AboutArabookverseContent;
  services: ServicesContent;
  howItWorks: HowItWorksContent;
  contact: ContactContent;
}

interface WebsiteContentContextType {
  content: WebsiteContent;
  updateSection: (section: keyof WebsiteContent, data: any) => void;
  resetToDefault: () => void;
  exportContent: () => void;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  saveToLocalStorage: () => void;
  clearLocalStorage: () => void;
  markAsUnsaved: () => void;
}

const defaultContent: WebsiteContent = {
  settings: {
    defaultLanguage: 'en',
  },
  hero: {
    title: {
      line1: {
        en: 'Transform Your Books Into',
        ar: 'حوّل كتبك إلى',
      },
      line2: {
        en: 'Digital Masterpieces',
        ar: 'روائع رقمية',
      },
    },
    subtitle: {
      en: 'Professional eBook formatting and audiobook production for modern publishers. Upload your manuscript and let us handle the digital transformation.',
      ar: 'تنسيق احترافي للكتب الإلكترونية وإنتاج الكتب الصوتية للناشرين المعاصرين. قم برفع مخطوطتك ودع لنا معالجة التحويل الرقمي.',
    },
    ctaButton: {
      en: 'Start Your Journey',
      ar: 'ابدأ رحلتك',
    },
    learnMoreButton: {
      en: 'Learn More',
      ar: 'اعرف المزيد',
    },
    stats: {
      booksPublished: {
        value: '500+',
        label: { en: 'Books Published', ar: 'كتاب منشور' },
      },
      publishers: {
        value: '50+',
        label: { en: 'Publishers', ar: 'ناشر' },
      },
      satisfaction: {
        value: '99%',
        label: { en: 'Satisfaction', ar: 'رضا العملاء' },
      },
    },
  },
  about: {
    title: { en: 'About ABVI', ar: 'عن ABVI' },
    subtitle: {
      en: 'Preserving Knowledge, Enabling Access',
      ar: 'حفظ المعرفة، تمكين الوصول',
    },
    description: {
      en: 'ABVI Publication Management System is dedicated to digitizing and publishing books, making knowledge accessible to readers worldwide. We work with authors, publishers, libraries, and cultural institutions to preserve and distribute valuable content.',
      ar: 'نظام إدارة النشر ABVI مكرس لرقمنة ونشر الكتب، وجعل المعرفة في متناول القراء في جميع أنحاء العالم. نعمل مع المؤلفين والناشرين والمكتبات والمؤسسات الثقافية للحفاظ على المحتوى القيم وتوزيعه.',
    },
  },
  aboutArabookverse: {
    title: {
      en: 'About Arabookverse',
      ar: 'عن أرابوكفيرس',
    },
    description: {
      en: 'Arabookverse is the leading Arabic audiobook publisher and distributor, operating the world\'s largest global network for Arabic audiobooks and ebooks. Through strategic partnerships with Bookwire (2023) and a landmark joint venture with the Sharjah Book Authority (2025), Arabookverse is redefining the Arabic digital content ecosystem with world-class infrastructure, deep publishing expertise, and strong regional insight. Backed by a leadership team with decades of experience across publishing, technology, and entrepreneurship, we are committed to shaping the future of Arabic storytelling—globally.',
      ar: 'تُعد Arabookverse الشركة الرائدة في نشر وتوزيع الكتب الصوتية العربية، حيث تدير أكبر شبكة عالمية للكتب الصوتية والإلكترونية العربية. ومن خلال شراكات استراتيجية مع Bookwire (2023)، وتأسيس مشروع مشترك محوري مع هيئة الشارقة للكتاب (2025)، تعيد Arabookverse رسم ملامح منظومة المحتوى العربي الرقمي عبر بنية تحتية عالمية المستوى، وخبرة عميقة في النشر، وفهم قوي لخصوصية المنطقة.\nويستند نجاحنا إلى فريق قيادي يمتلك عقودًا من الخبرة في مجالات النشر والتكنولوجيا وريادة الأعمال، ملتزمين بتشكيل مستقبل السرد القصصي والأدبي العربي على المستوى العالمي.',
    },
    vision: {
      title: { en: 'Our Vision', ar: 'رؤيتنا' },
      description: {
        en: 'Leading global Arabic digital content, connecting creators, readers, and listeners to the richness of Arabic literature and storytelling.',
        ar: 'قيادة المحتوى العربي الرقمي، وربط المبدعين والقراء والمستمعين بثراء الأدب العربي وفنونه السردية.',
      },
    },
    mission: {
      title: { en: 'Our Mission', ar: 'رسالتنا' },
      description: {
        en: 'Expanding Arabic digital content and making it accessible anytime, anywhere, for everyone.',
        ar: 'توسيع نطاق المحتوى العربي الرقمي وجعله متاحًا للجميع، في أي وقت ومن أي مكان.',
      },
    },
    purpose: {
      title: { en: 'Our Purpose', ar: 'غايتنا' },
      items: [
        { en: 'Enable Arab creators', ar: 'تمكين المبدعين العرب' },
        { en: 'Empower Arab readers', ar: 'دعم وتمكين القرّاء العرب' },
        { en: 'Promote global access to Arabic content', ar: 'تعزيز الوصول العالمي إلى المحتوى العربي' },
        { en: 'Preserve and elevate the Arabic language worldwide', ar: 'الحفاظ على الهوية العالمية للغة العربية والارتقاء بها' },
      ],
    },
  },
  services: {
    title: { en: 'Our Services', ar: 'خدماتنا' },
    subtitle: { en: 'Comprehensive publishing solutions', ar: 'حلول نشر شاملة' },
    services: [
      {
        title: { en: 'Book Digitization', ar: 'رقمنة الكتب' },
        description: {
          en: 'Convert physical books into high-quality digital formats',
          ar: 'تحويل الكتب المادية إلى صيغ رقمية عالية الجودة',
        },
        icon: 'book',
      },
      {
        title: { en: 'Digital Publishing', ar: 'النشر الرقمي' },
        description: {
          en: 'Professional formatting and publication across multiple platforms',
          ar: 'تنسيق ونشر احترافي عبر منصات متعددة',
        },
        icon: 'upload',
      },
      {
        title: { en: 'Distribution', ar: 'التوزيع' },
        description: {
          en: 'Reach readers through our extensive distribution network',
          ar: 'الوصول إلى القراء من خلال شبكة توزيع واسعة',
        },
        icon: 'share',
      },
      {
        title: { en: 'Content Preservation', ar: 'حفظ المحتوى' },
        description: {
          en: 'Secure archival and long-term preservation of valuable content',
          ar: 'أرشفة آمنة وحفظ طويل الأمد للمحتوى القيم',
        },
        icon: 'archive',
      },
    ],
  },
  howItWorks: {
    title: { en: 'How It Works', ar: 'كيف يعمل' },
    subtitle: { en: 'Simple steps to publish your book', ar: 'خطوات بسيطة لنشر كتابك' },
    videoUrl: '',
    steps: [
      {
        title: { en: 'Submit Your Content', ar: 'أرسل محتواك' },
        description: {
          en: 'Upload your manuscript, scanned pages, or digital files through our secure platform',
          ar: 'قم برفع مخطوطتك أو الصفحات الممسوحة ضوئياً أو الملفات الرقمية عبر منصتنا الآمنة',
        },
        icon: 'upload',
      },
      {
        title: { en: 'Professional Processing', ar: 'معالجة احترافية' },
        description: {
          en: 'Our team digitizes, formats, and prepares your content for publication',
          ar: 'يقوم فريقنا برقمنة وتنسيق وإعداد محتواك للنشر',
        },
        icon: 'settings',
      },
      {
        title: { en: 'Review & Approve', ar: 'المراجعة والموافقة' },
        description: {
          en: 'Review the formatted version and request any necessary revisions',
          ar: 'راجع النسخة المنسقة واطلب أي تعديلات ضرورية',
        },
        icon: 'check',
      },
      {
        title: { en: 'Publish & Distribute', ar: 'النشر والتوزيع' },
        description: {
          en: 'Your book goes live and becomes available across multiple platforms',
          ar: 'يصبح كتابك متاحاً عبر منصات متعددة',
        },
        icon: 'globe',
      },
    ],
  },
  contact: {
    title: { en: 'Get in Touch', ar: 'تواصل معنا' },
    subtitle: {
      en: "Let's discuss your publishing needs",
      ar: 'دعنا نناقش احتياجات النشر الخاصة بك',
    },
    email: 'contact@abvi.com',
    phone: '+966 XX XXX XXXX',
    address: {
      en: 'Riyadh, Saudi Arabia',
      ar: 'الرياض، المملكة العربية السعودية',
    },
  },
};

const WebsiteContentContext = createContext<WebsiteContentContextType | undefined>(
  undefined
);

export const useWebsiteContent = () => {
  const context = useContext(WebsiteContentContext);
  if (!context) {
    throw new Error('useWebsiteContent must be used within WebsiteContentProvider');
  }
  return context;
};

interface WebsiteContentProviderProps {
  children: ReactNode;
}

const LOCALSTORAGE_KEY = 'cms-unsaved-changes';

export const WebsiteContentProvider: React.FC<WebsiteContentProviderProps> = ({
  children,
}) => {
  const [content, setContent] = useState<WebsiteContent>(defaultContent);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [originalContent, setOriginalContent] = useState<WebsiteContent>(defaultContent);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load content from JSON files on mount - ONLY source of truth
  useEffect(() => {
    const loadContentFromFiles = async () => {
      try {
        console.log('Loading content from JSON files...');

        // Fetch both language files
        const [enResponse, arResponse] = await Promise.all([
          fetch('/content/content-en.json'),
          fetch('/content/content-ar.json'),
        ]);

        if (enResponse.ok && arResponse.ok) {
          const enData = await enResponse.json();
          const arData = await arResponse.json();

          // Merge the language-specific data back into bilingual structure
          const mergedContent = mergeLanguageData(enData, arData);

          console.log('Content loaded successfully:', {
            servicesCount: mergedContent.services.services?.length || 0,
            stepsCount: mergedContent.howItWorks.steps?.length || 0
          });

          // Check for any unsaved changes in localStorage
          const savedChanges = localStorage.getItem(LOCALSTORAGE_KEY);
          if (savedChanges) {
            try {
              const unsavedContent = JSON.parse(savedChanges);
              console.log('Found unsaved changes in localStorage, applying...', {
                hasHeroChanges: unsavedContent.hero?.title !== mergedContent.hero?.title,
                savedHeroTitle: unsavedContent.hero?.title?.line1?.en,
                originalHeroTitle: mergedContent.hero?.title?.line1?.en
              });
              setContent(unsavedContent);
              setOriginalContent(mergedContent);
              setHasUnsavedChanges(true);
            } catch (error) {
              console.error('Error parsing unsaved changes:', error);
              setContent(mergedContent);
              setOriginalContent(mergedContent);
            }
          } else {
            console.log('No unsaved changes found in localStorage');
            setContent(mergedContent);
            setOriginalContent(mergedContent);
          }
          setHasLoaded(true);
        } else {
          console.error('Content files not found! Using default content');
          // Check for unsaved changes even when using default content
          const savedChanges = localStorage.getItem(LOCALSTORAGE_KEY);
          if (savedChanges) {
            try {
              const unsavedContent = JSON.parse(savedChanges);
              setContent(unsavedContent);
              setOriginalContent(defaultContent);
              setHasUnsavedChanges(true);
            } catch (error) {
              setContent(defaultContent);
              setOriginalContent(defaultContent);
            }
          } else {
            setContent(defaultContent);
            setOriginalContent(defaultContent);
          }
          setHasLoaded(true);
        }
      } catch (error) {
        console.error('Error loading content files:', error);
        setContent(defaultContent);
        setOriginalContent(defaultContent);
        setHasLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (!hasLoaded) {
      loadContentFromFiles();
    }
  }, [hasLoaded]);

  const updateSection = (section: keyof WebsiteContent, data: any) => {
    const updatedContent = {
      ...content,
      [section]: data,
    };
    setContent(updatedContent);

    // Save to localStorage only when explicitly called
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(updatedContent));
    setHasUnsavedChanges(true);
    console.log(`Updated ${section} and saved to localStorage`);
  };

  // Check if content has changed compared to original
  useEffect(() => {
    if (hasLoaded) {
      const hasChanges = JSON.stringify(content) !== JSON.stringify(originalContent);
      setHasUnsavedChanges(hasChanges);
    }
  }, [content, originalContent, hasLoaded]);

  const resetToDefault = () => {
    setContent(defaultContent);
    setOriginalContent(defaultContent);
    localStorage.removeItem(LOCALSTORAGE_KEY);
    setHasUnsavedChanges(false);
  };

  const saveToLocalStorage = () => {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(content));
    setHasUnsavedChanges(true);
    console.log('Content manually saved to localStorage');
  };

  const clearLocalStorage = () => {
    localStorage.removeItem(LOCALSTORAGE_KEY);
    setOriginalContent(content); // Mark current content as the new baseline
    setHasUnsavedChanges(false);
    console.log('LocalStorage cleared - changes considered saved');
  };

  const markAsUnsaved = () => {
    setHasUnsavedChanges(true);
  };

  const exportContent = () => {
    // Split content into two language files
    const enContent = extractLanguageData(content, 'en');
    const arContent = extractLanguageData(content, 'ar');

    // Download English file
    const enBlob = new Blob([JSON.stringify(enContent, null, 2)], {
      type: 'application/json',
    });
    const enUrl = URL.createObjectURL(enBlob);
    const enLink = document.createElement('a');
    enLink.href = enUrl;
    enLink.download = 'content-en.json';
    document.body.appendChild(enLink);
    enLink.click();
    document.body.removeChild(enLink);
    URL.revokeObjectURL(enUrl);

    // Download Arabic file (with small delay)
    setTimeout(() => {
      const arBlob = new Blob([JSON.stringify(arContent, null, 2)], {
        type: 'application/json',
      });
      const arUrl = URL.createObjectURL(arBlob);
      const arLink = document.createElement('a');
      arLink.href = arUrl;
      arLink.download = 'content-ar.json';
      document.body.appendChild(arLink);
      arLink.click();
      document.body.removeChild(arLink);
      URL.revokeObjectURL(arUrl);

      // Clear localStorage after successful export
      clearLocalStorage();
      console.log('Content exported successfully and localStorage cleared');
    }, 100);
  };

  const importContent = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileReaders: { [key: string]: any } = {};

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          const fileName = file.name;

          if (fileName.includes('en')) {
            fileReaders.en = data;
          } else if (fileName.includes('ar')) {
            fileReaders.ar = data;
          }

          // If both files are loaded, merge them
          if (fileReaders.en && fileReaders.ar) {
            const mergedContent = mergeLanguageData(fileReaders.en, fileReaders.ar);
            setContent(mergedContent);
          }
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      };
      reader.readAsText(file);
    });
  };

  // Helper function to extract language-specific data
  const extractLanguageData = (data: any, lang: 'en' | 'ar'): any => {
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      // Preserve settings as-is (language-agnostic)
      if ('settings' in data && data.settings) {
        const result: any = { settings: data.settings };
        for (const key in data) {
          if (key !== 'settings') {
            result[key] = extractLanguageData(data[key], lang);
          }
        }
        return result;
      }

      if ('en' in data && 'ar' in data) {
        return data[lang];
      }
      const result: any = {};
      for (const key in data) {
        result[key] = extractLanguageData(data[key], lang);
      }
      return result;
    }
    return data;
  };

  // Helper function to convert object-array to real array
  const objectToArray = (obj: any): any[] => {
    // Already an array? Return as-is
    if (Array.isArray(obj)) {
      return obj;
    }

    // Not an object or null? Return empty array
    if (typeof obj !== 'object' || obj === null) {
      return [];
    }

    // Convert object with numeric keys to array
    const keys = Object.keys(obj).filter(key => /^\d+$/.test(key)).sort((a, b) => parseInt(a) - parseInt(b));
    return keys.map(key => obj[key]);
  };

  // Helper function to merge language data
  const mergeLanguageData = (enData: any, arData: any): WebsiteContent => {
    // Convert object-arrays to real arrays with safe fallbacks
    const enServices = objectToArray(enData?.services?.services || {});
    const arServices = objectToArray(arData?.services?.services || {});
    const enSteps = objectToArray(enData?.howItWorks?.steps || {});
    const arSteps = objectToArray(arData?.howItWorks?.steps || {});
    const enPurposeItems = objectToArray(enData?.aboutArabookverse?.purpose?.items || {});
    const arPurposeItems = objectToArray(arData?.aboutArabookverse?.purpose?.items || {});

    // Extract settings from either file (prefer English file, fallback to Arabic, then default)
    const settings = enData?.settings || arData?.settings || defaultContent.settings;

    return {
      settings,
      hero: {
        title: {
          line1: { en: enData.hero.title.line1, ar: arData.hero.title.line1 },
          line2: { en: enData.hero.title.line2, ar: arData.hero.title.line2 },
        },
        subtitle: { en: enData.hero.subtitle, ar: arData.hero.subtitle },
        ctaButton: { en: enData.hero.ctaButton, ar: arData.hero.ctaButton },
        learnMoreButton: { en: enData.hero.learnMoreButton, ar: arData.hero.learnMoreButton },
        stats: {
          booksPublished: {
            value: enData.hero.stats.booksPublished.value,
            label: {
              en: enData.hero.stats.booksPublished.label,
              ar: arData.hero.stats.booksPublished.label,
            },
          },
          publishers: {
            value: enData.hero.stats.publishers.value,
            label: {
              en: enData.hero.stats.publishers.label,
              ar: arData.hero.stats.publishers.label,
            },
          },
          satisfaction: {
            value: enData.hero.stats.satisfaction.value,
            label: {
              en: enData.hero.stats.satisfaction.label,
              ar: arData.hero.stats.satisfaction.label,
            },
          },
        },
      },
      about: {
        title: { en: enData.about.title, ar: arData.about.title },
        subtitle: { en: enData.about.subtitle, ar: arData.about.subtitle },
        description: { en: enData.about.description, ar: arData.about.description },
      },
      aboutArabookverse: {
        title: {
          en: enData?.aboutArabookverse?.title || defaultContent.aboutArabookverse.title.en,
          ar: arData?.aboutArabookverse?.title || defaultContent.aboutArabookverse.title.ar,
        },
        description: {
          en: enData?.aboutArabookverse?.description || defaultContent.aboutArabookverse.description.en,
          ar: arData?.aboutArabookverse?.description || defaultContent.aboutArabookverse.description.ar,
        },
        vision: {
          title: {
            en: enData?.aboutArabookverse?.vision?.title || defaultContent.aboutArabookverse.vision.title.en,
            ar: arData?.aboutArabookverse?.vision?.title || defaultContent.aboutArabookverse.vision.title.ar,
          },
          description: {
            en: enData?.aboutArabookverse?.vision?.description || defaultContent.aboutArabookverse.vision.description.en,
            ar: arData?.aboutArabookverse?.vision?.description || defaultContent.aboutArabookverse.vision.description.ar,
          },
        },
        mission: {
          title: {
            en: enData?.aboutArabookverse?.mission?.title || defaultContent.aboutArabookverse.mission.title.en,
            ar: arData?.aboutArabookverse?.mission?.title || defaultContent.aboutArabookverse.mission.title.ar,
          },
          description: {
            en: enData?.aboutArabookverse?.mission?.description || defaultContent.aboutArabookverse.mission.description.en,
            ar: arData?.aboutArabookverse?.mission?.description || defaultContent.aboutArabookverse.mission.description.ar,
          },
        },
        purpose: {
          title: {
            en: enData?.aboutArabookverse?.purpose?.title || defaultContent.aboutArabookverse.purpose.title.en,
            ar: arData?.aboutArabookverse?.purpose?.title || defaultContent.aboutArabookverse.purpose.title.ar,
          },
          items: enPurposeItems.length > 0
            ? enPurposeItems.map((item: any, index: number) => ({
                en: typeof item === 'string' ? item : item.en || item,
                ar: arPurposeItems[index]
                  ? (typeof arPurposeItems[index] === 'string' ? arPurposeItems[index] : arPurposeItems[index].ar || arPurposeItems[index])
                  : '',
              }))
            : defaultContent.aboutArabookverse.purpose.items,
        },
      },
      services: {
        title: { en: enData.services.title, ar: arData.services.title },
        subtitle: { en: enData.services.subtitle, ar: arData.services.subtitle },
        services: enServices.map((service: any, index: number) => ({
          title: { en: service.title, ar: arServices[index].title },
          description: {
            en: service.description,
            ar: arServices[index].description,
          },
          icon: service.icon,
        })),
      },
      howItWorks: {
        title: { en: enData.howItWorks.title, ar: arData.howItWorks.title },
        subtitle: { en: enData.howItWorks.subtitle, ar: arData.howItWorks.subtitle },
        videoUrl: enData.howItWorks.videoUrl,
        steps: enSteps.map((step: any, index: number) => ({
          title: { en: step.title, ar: arSteps[index].title },
          description: {
            en: step.description,
            ar: arSteps[index].description,
          },
          icon: step.icon,
        })),
      },
      contact: {
        title: { en: enData.contact.title, ar: arData.contact.title },
        subtitle: { en: enData.contact.subtitle, ar: arData.contact.subtitle },
        email: enData.contact.email,
        phone: enData.contact.phone,
        address: { en: enData.contact.address, ar: arData.contact.address },
      },
    };
  };

  return (
    <WebsiteContentContext.Provider
      value={{
        content,
        updateSection,
        resetToDefault,
        exportContent,
        isLoading,
        hasUnsavedChanges,
        saveToLocalStorage,
        clearLocalStorage,
        markAsUnsaved,
      }}
    >
      {children}
    </WebsiteContentContext.Provider>
  );
};
